import { useCallback, useEffect, useState } from 'react';
import update from 'immutability-helper';
import { DropdownProvider } from '../context/DropdownContext';
import Dropdown from '../core/Dropdown';
import Button from '../legacy/core/Button';
import { POST, request } from '../../wp_helpers/hooks/useFetch';
import { notification } from '../../utils/utils';
import FilterItem from './FilterItem';
import { useTranslations } from '../context/TranslationContext';
import './index.less';
import logAbortableError from '@helpers/logAbortableError';

const CustomColumnsDropdown = ({ apiUrl, enableDnd = true }) => {
    const { t } = useTranslations();
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFields, setSelectedFields] = useState([]);

    useEffect(() => {
        fetchColumns();
    }, []);

    const onSaveHandler = async () => {
        if (_.isEmpty(selectedFields)) {
            return notification(t('Please select the column'), { type: 'warning' });
        }
        updateColumns(selectedFields);
    };

    const isSelected = (field) => selectedFields.includes(field);

    const updateColumns = async (selectedFields) => {
        try {
            setLoading(true);
            const sortedColumns = [];
            _.each(columns, ({ result_key, key }) => {
                const id = result_key || key;
                if (_.includes(selectedFields, id)) {
                    sortedColumns.push(id);
                }
            });
            const { message } = await request(apiUrl, {
                method: POST,
                payload: JSON.stringify({ columns_data: sortedColumns }),
            });
            notification(message, { type: 'success' });
            window.location.reload();
        } catch (e) {
            logAbortableError('Invalid API call', e);
            notification(t('Error occurred, please try again.'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const onChangeHandler = (id) => {
        setSelectedFields((prevData) => {
            if (prevData.includes(id)) {
                return prevData.filter((field) => field !== id);
            }
            return [...prevData, id];
        });
    };

    const fetchColumns = async () => {
        try {
            const { columns_data } = await request(apiUrl);
            setColumns(columns_data);
            const _selectedFields = _.filter(columns_data, 'selected');
            setSelectedFields(_.map(_selectedFields, ({ result_key, key }) => result_key || key));
        } catch (e) {
            logAbortableError('Error fetching columns', e);
        }
    };

    const resetColumns = () => {
        updateColumns([]);
    };

    const dragElement = useCallback((dragIndex, hoverIndex) => {
        setColumns((prevCards) => {
            const draggedCard = prevCards[dragIndex];
            const updatedCards = update(prevCards, {
                $splice: [
                    [dragIndex, 1],
                    [hoverIndex, 0, draggedCard],
                ],
            });
            return updatedCards;
        });
    }, []);
    
    return (
        <div className='custom-columns-container'>
            <DropdownProvider>
                <Dropdown
                    id={`custom-columns-dropdown`}
                    className={'custom-columns-dropdown with-scroll'}
                    btnClass='btn btn-default custom-columns-btn'
                    icon='icon-svg icon-svg-columns'
                    label={t('Customize')}
                    withScroll
                >
                    {({ toggleDropdown }) => (
                        <>
                            <li className='header-item'>
                                <a className='btn btn-default btn-block' onClick={resetColumns}>
                                    <span className='icon-svg icon-svg-refresh-cw-05'></span>
                                    {t('Reset to default')}
                                </a>
                            </li>
                            {_.map(columns, ({ key, result_key, title }, index) => {
                                const id = result_key || key;
                                return (
                                    <FilterItem
                                        key={id}
                                        index={index}
                                        id={id}
                                        title={title}
                                        dragElement={dragElement}
                                        isSelected={isSelected(id)}
                                        onChange={onChangeHandler}
                                    />
                                );
                            })}
                            <li className='footer-item'>
                                <Button
                                    type='default'
                                    className='btn-block btn-flat'
                                    disabled={_.isEmpty(selectedFields) || loading}
                                    onClick={() => {
                                        onSaveHandler();
                                        toggleDropdown();
                                    }}
                                    title={t('Done')}
                                />
                            </li>
                        </>
                    )}
                </Dropdown>
            </DropdownProvider>
        </div>
    );
};

export default CustomColumnsDropdown;
